import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type FC,
} from 'react'
import ReactDOM from 'react-dom'
import SunEditor from 'suneditor-react'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import 'suneditor/dist/css/suneditor.min.css'
import 'mathlive'
import FunctionsIcon from '@mui/icons-material/Functions'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import './styles/TextAreaNewVersion.css'
import {
  ALLOWED_TAGS,
  BUTTON_LIST,
  BUTTON_LIST_SIMPLE,
  DEFAULT_STYLE,
  LATEX_SPAN_CSS_STYLES,
  MF_CSS_STYLES,
  MF_SHORTCUTS,
  WHITE_LIST_TAGS,
} from '@/components/UI/TextAreaNewVersion/config'
import {
  BrainlyMathEditorProps,
  MathfieldElement,
} from '@/components/UI/TextAreaNewVersion/types'
import { markdownToHtml, htmlToMarkdown } from '@/utils/markdownConverter'
import MarkdownViewer from '@/components/UI/TextAreaNewVersion/MarkdownViewer'
import ApiUtil from '@/utils/apiUtil'

const BrainlyMathEditor: FC<BrainlyMathEditorProps> = ({
  value = '',
  onChange,
  placeholder = '',
  height = '300px',
  minHeight = '200px',
  disabled = false,
  readOnly = false,
  mode = 'classic',
  maxCharCount = null,
  variant = 'full',
}) => {
  // ============================================================================
  // Track manual keyboard close to prevent auto-reopen
  // ============================================================================
  const manuallyClosedRef = useRef(false)

  // ============================================================================
  // Helper: Add close button to Math keyboard
  // ============================================================================

  const injectCloseButtonToKeyboard = useCallback(() => {
    const keyboard = document.querySelector('.ML__keyboard') as HTMLElement
    if (!keyboard) return

    // Check if close button already exists
    if (keyboard.querySelector('.math-keyboard-close-btn')) return

    // Create close button with red background
    const closeBtn = document.createElement('button')
    closeBtn.className = 'math-keyboard-close-btn'
    closeBtn.setAttribute('aria-label', 'Close keyboard')
    closeBtn.setAttribute('title', 'Close keyboard')
    closeBtn.setAttribute('type', 'button') // Prevent form submission

    // Style the button - RED color for visibility
    closeBtn.style.cssText = `
      position: absolute;
      top: 8px;
      right: 8px;
      width: 36px;
      height: 36px;
      border: none;
      background: #D71E30;
      color: white;
      font-size: 28px;
      line-height: 1;
      cursor: pointer;
      border-radius: 50%;
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      font-weight: bold;
    `

    // Render MUI KeyboardArrowDown icon using ReactDOM
    ReactDOM.render(
      <KeyboardArrowDownIcon sx={{ fontSize: 24, color: 'white' }} />,
      closeBtn,
    )

    // Hover effect - darker red
    closeBtn.addEventListener('mouseenter', () => {
      closeBtn.style.background = '#B71C28'
      closeBtn.style.transform = 'scale(1.1)'
    })
    closeBtn.addEventListener('mouseleave', () => {
      closeBtn.style.background = '#D71E30'
      closeBtn.style.transform = 'scale(1)'
    })

    // Click handler - hide keyboard WITHOUT blurring math-field
    // IMPORTANT: Do NOT blur the math-field, as it triggers premature conversion
    // Just hide the keyboard, user can keep editing or click outside to blur naturally
    const handleClose = (e: Event) => {
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()

      try {
        // Set flag to prevent auto-reopen
        manuallyClosedRef.current = true

        // Approach 1: Use mathVirtualKeyboard API (recommended)
        const mathKeyboard = (window as any).mathVirtualKeyboard
        if (mathKeyboard) {
          mathKeyboard.hide()
        }

        // Approach 2: Force hide via CSS (backup)
        const keyboardEl = document.querySelector(
          '.ML__keyboard',
        ) as HTMLElement
        if (keyboardEl) {
          keyboardEl.style.display = 'none'
          keyboardEl.classList.remove('is-visible')
        }

        // DO NOT blur the math-field - this was causing LaTeX to lose format
        // The math-field should remain focused so user can continue editing
        // Conversion will happen naturally when user clicks outside
      } catch (error) {
        console.error('[Close Button] Error hiding keyboard:', error)
      }
    }

    closeBtn.addEventListener('click', handleClose, { capture: true })
    closeBtn.addEventListener('mousedown', handleClose, { capture: true })
    closeBtn.addEventListener('touchstart', handleClose, { capture: true })

    // Make button always on top and clickable
    closeBtn.style.pointerEvents = 'auto'

    // Append to keyboard - use appendChild to ensure it's rendered
    keyboard.appendChild(closeBtn)
  }, [])

  // ============================================================================
  // Compute ButtonList based on variant
  // ============================================================================

  const buttonList = useMemo(() => {
    if (variant === 'simple') {
      // Simplified toolbar - basic formatting + link
      return BUTTON_LIST_SIMPLE
    }
    // Full toolbar with all options (default)
    return BUTTON_LIST || []
  }, [variant])

  // ============================================================================
  // Image Upload Handler
  // ============================================================================

  /**
   * Upload image to server instead of converting to base64
   *
   * Pattern from SubEditor.tsx (working implementation in this codebase)
   * Response format: { result: [{ url, name, size }] }
   */
  const handleImageUploadBefore = useCallback(
    (files: File[], info: any, uploadHandler: any) => {
      if (!files || files.length === 0) {
        console.warn('[BrainlyMathEditor] ⚠️ No files to upload')
        return
      }

      const file = files[0]
      const formData = new FormData()
      formData.append('media', file)

      ApiUtil.SendAsync<any>({
        url: `media/images/upload`,
        method: 'POST',
        data: formData,
      })
        .then(json => {
          if (json.status === 200 && json.data?.data?.media?.url) {
            const imageUrl = json.data.data.media.url

            // Response with proper image size constraints
            const response = {
              errorMessage: 'Image could not be uploaded',
              result: [
                {
                  url: imageUrl,
                  name: json.data.data.media.filename || file.name,
                  size: file.size.toString(),
                },
              ],
            }

            uploadHandler(response)
          } else {
            console.error('[BrainlyMathEditor] ❌ Upload failed:', json)
          }
        })
        .catch(error => {
          console.error('[BrainlyMathEditor] ❌ Upload error:', error)
        })
    },
    [],
  )

  // ============================================================================
  // Markdown <-> HTML Conversion
  // ============================================================================

  /**
   * Convert markdown input to HTML for editor display
   */
  const markdownToEditorHtml = useCallback((markdown: string): string => {
    if (!markdown || markdown.trim() === '') return ''

    return markdownToHtml(markdown)
  }, [])

  /**
   * Convert editor HTML output to markdown
   */
  const editorHtmlToMarkdown = useCallback((html: string): string => {
    if (!html || html.trim() === '') return ''
    return htmlToMarkdown(html)
  }, [])

  // ============================================================================
  // State & Refs
  // ============================================================================

  const editorInstanceRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [buttonsInjected, setButtonsInjected] = useState(false)
  const focusedMathFieldRef = useRef<MathfieldElement | null>(null)
  const conversionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isEditingMath = useRef(false)
  const isInternalChangeRef = useRef(false)
  const initialContentSetRef = useRef(false)

  /**
   * HTML content for editor (internal state)
   * Editor always works with HTML, conversion happens at input/output boundaries
   */
  const [editorContent, setEditorContent] = useState<string>(() =>
    markdownToEditorHtml(value),
  )

  /**
   * Track last emitted markdown content to avoid duplicate onChange calls
   */
  const lastContentRef = useRef<string>(value)

  /**
   * Track last synced value from props to avoid infinite loop
   */
  const lastSyncedValueRef = useRef<string>(value)

  // Suppress ResizeObserver error (benign warning from MathLive/SunEditor)
  useEffect(() => {
    const errorHandler = (e: ErrorEvent) => {
      if (
        e.message.includes('ResizeObserver loop') ||
        e.message.includes(
          'ResizeObserver loop completed with undelivered notifications',
        )
      ) {
        e.stopImmediatePropagation()
        e.preventDefault()
        return false
      }
    }

    // Also catch unhandled promise rejections related to ResizeObserver
    const unhandledRejectionHandler = (e: PromiseRejectionEvent) => {
      if (
        e.reason?.message?.includes('ResizeObserver') ||
        e.reason?.toString()?.includes('ResizeObserver')
      ) {
        e.preventDefault()
        return false
      }
    }

    window.addEventListener('error', errorHandler)
    window.addEventListener('unhandledrejection', unhandledRejectionHandler)

    return () => {
      window.removeEventListener('error', errorHandler)
      window.removeEventListener(
        'unhandledrejection',
        unhandledRejectionHandler,
      )
    }
  }, [])

  /**
   * Emit change event - convert HTML to markdown and call onChange
   */
  const emitChange = useCallback(
    (htmlContent: string) => {
      if (typeof onChange !== 'function') return

      const markdownContent = editorHtmlToMarkdown(htmlContent)
      lastContentRef.current = markdownContent
      lastSyncedValueRef.current = markdownContent
      onChange(markdownContent)
    },
    [onChange, editorHtmlToMarkdown],
  )

  // Handle editor content changes (convert HTML to markdown on emit)
  const handleChange = useCallback(
    (htmlContent: string) => {
      if (isEditingMath.current) return

      // Mark as internal change to prevent overwriting
      isInternalChangeRef.current = true
      setEditorContent(htmlContent)
      emitChange(htmlContent)
    },
    [emitChange],
  )

  /**
   * Configure mathfield after mounting
   */
  const configureMathField = useCallback((mf: MathfieldElement) => {
    try {
      if (document.contains(mf)) {
        // Enable full editing capabilities
        mf.mathVirtualKeyboardPolicy = 'manual'
        mf.smartMode = true
        mf.smartFence = true
        mf.smartSuperscript = true
        mf.inlineShortcuts = MF_SHORTCUTS
        // Ensure editable by removing readonly if set
        mf.removeAttribute('readonly')
        mf.removeAttribute('disabled')
      }
    } catch (error) {
      console.warn('Failed to initialize MathLive shortcuts:', error)
    }
  }, [])

  /**
   * Create an editable mathfield
   */
  const createMathField = useCallback(
    (latex: string = ''): MathfieldElement => {
      const mf = document.createElement('math-field') as MathfieldElement
      mf.textContent = latex
      mf.setAttribute('virtual-keyboard-mode', 'auto')
      mf.style.cssText = MF_CSS_STYLES
      mf.classList.add('ml-field', 'editing')
      return mf
    },
    [],
  )

  /**
   * Safely convert mathfield to LaTeX span
   */
  const convertMathFieldToLatex = useCallback(
    (mf: MathfieldElement) => {
      if (!document.contains(mf)) return

      try {
        const latex = mf.value || mf.textContent || ''

        // Do not convert if the formula is incomplete
        if (latex.includes('\placeholder')) {
          return
        }

        if (latex.trim()) {
          // Create LaTeX span element with better styling
          const latexSpan = document.createElement('span')
          latexSpan.setAttribute('data-latex', latex)
          latexSpan.className = 'math-content'
          latexSpan.style.cssText =
            LATEX_SPAN_CSS_STYLES + ' position: relative; padding-right: 24px;'
          latexSpan.setAttribute('contenteditable', 'false')
          latexSpan.title = 'Click to edit, or press Delete/Backspace to remove'

          // Add delete button
          const deleteBtn = document.createElement('span')
          deleteBtn.className = 'math-delete-btn'
          deleteBtn.innerHTML = '×'
          deleteBtn.style.cssText =
            'position: absolute; right: 2px; top: 50%; transform: translateY(-50%); ' +
            'width: 18px; height: 18px; line-height: 16px; text-align: center; ' +
            'background: #ef4444; color: white; border-radius: 50%; ' +
            'cursor: pointer; font-size: 14px; font-weight: bold; ' +
            'display: none; z-index: 10;'
          deleteBtn.title = 'Delete formula'

          // Show delete button on hover
          latexSpan.addEventListener('mouseenter', () => {
            latexSpan.style.backgroundColor = '#e0f2fe'
            latexSpan.style.borderColor = '#0284c7'
            latexSpan.style.transform = 'scale(1.02)'
            deleteBtn.style.display = 'block'
          })

          latexSpan.addEventListener('mouseleave', () => {
            latexSpan.style.backgroundColor = '#f0f9ff'
            latexSpan.style.borderColor = '#0ea5e9'
            latexSpan.style.transform = 'scale(1)'
            deleteBtn.style.display = 'none'
          })

          // Handle delete button click
          deleteBtn.addEventListener('click', e => {
            e.preventDefault()
            e.stopPropagation()

            if (latexSpan.parentNode) {
              latexSpan.parentNode.removeChild(latexSpan)

              // Update editor content
              setTimeout(() => {
                if (editorInstanceRef.current?.getContents) {
                  const updatedContent = editorInstanceRef.current.getContents()
                  setEditorContent(updatedContent)
                  emitChange(updatedContent)
                }
              }, 50)
            }
          })

          latexSpan.appendChild(deleteBtn)

          // Note: Click handler is handled via event delegation in useEffect
          // to avoid duplicate handlers and potential infinite loops
          // Render LaTeX with KaTeX, then inject the raw LaTeX into the katex-mathml span for persistence
          try {
            const renderedHtml = katex.renderToString(latex, {
              throwOnError: false,
              displayMode: false,
              strict: false,
              trust: true,
              macros: {
                '\f': '#1f(#2)',
                '\RR': '\mathbb{R}',
                '\NN': '\mathbb{N}',
                '\ZZ': '\mathbb{Z}',
                '\QQ': '\mathbb{Q}',
                '\CC': '\mathbb{C}',
              },
            })
            // Inject raw latex into the mathml span for retrieval later
            latexSpan.innerHTML = renderedHtml.replace(
              /<span class="katex-mathml">.*?<\/span>/,
              `<span class="katex-mathml">${latex}</span>`,
            )
          } catch (error) {
            console.warn('KaTeX rendering failed for LaTeX:', latex, error)
            // Fallback to raw latex text if KaTeX fails
            latexSpan.textContent = latex
          }

          // Ensure spacing around the math token to avoid sticking text
          const parentNode = mf.parentNode
          if (parentNode) {
            const prev = mf.previousSibling
            const next = mf.nextSibling
            const needsSpaceBefore =
              !prev ||
              (prev.nodeType === Node.TEXT_NODE
                ? !/\s$/.test(prev.textContent || '')
                : true)
            const needsSpaceAfter =
              !next ||
              (next.nodeType === Node.TEXT_NODE
                ? !/^[ ​]/.test(next.textContent || '')
                : true)

            if (needsSpaceBefore) {
              parentNode.insertBefore(document.createTextNode('\u00A0'), mf)
            }
            if (needsSpaceAfter) {
              parentNode.insertBefore(
                document.createTextNode('\u00A0'),
                mf.nextSibling,
              )
            }

            // Replace mathfield with LaTeX span
            parentNode.replaceChild(latexSpan, mf)
          }

          // Update editor content after DOM change
          setTimeout(() => {
            try {
              if (
                !editorInstanceRef.current ||
                !editorInstanceRef.current.getContents
              ) {
                console.warn('Editor instance not ready for content update')
                return
              }

              const updatedContent = editorInstanceRef.current.getContents()

              // Only update state if content actually changed
              const currentMarkdown = editorHtmlToMarkdown(updatedContent)
              if (currentMarkdown !== lastContentRef.current) {
                setEditorContent(updatedContent)
                emitChange(updatedContent)
              }
            } catch (error) {
              console.warn('Failed to update content after conversion:', error)
            }
          }, 50)
        }
      } catch (error) {
        console.warn('Failed to convert mathfield to LaTeX:', error)
      }
    },
    [emitChange, editorHtmlToMarkdown],
  )

  /**
   * Process existing content to convert LaTeX spans back to editable mathfields
   */
  const processExistingContent = useCallback(() => {
    if (
      !editorInstanceRef.current ||
      !editorInstanceRef.current.core?.context?.element?.wysiwyg
    ) {
      return
    }
    try {
      const wysiwyg = editorInstanceRef.current.core.context.element.wysiwyg

      // Find all rendered KaTeX blocks by their main class (old format)
      const katexSpans = wysiwyg.querySelectorAll('span.katex')

      katexSpans.forEach((span: HTMLElement) => {
        try {
          // Find the child element that holds the hidden LaTeX source
          const mathmlSpan = span.querySelector('span.katex-mathml')
          const latex = mathmlSpan?.textContent

          if (latex) {
            // Create new mathfield with the retrieved LaTeX
            const mf = createMathField(latex)

            // Replace the entire KaTeX span with the editable mathfield
            if (span.parentNode) {
              span.parentNode.replaceChild(mf, span)
            }

            // Configure the new mathfield
            setTimeout(() => {
              try {
                if (document.contains(mf)) {
                  configureMathField(mf)
                }
              } catch (error) {
                console.warn(
                  'Failed to configure mathfield after insertion:',
                  error,
                )
              }
            }, 50)
          }
        } catch (error) {
          console.warn('Failed to process katex span:', error)
        }
      })

      // Find all math-content spans (new format from convertMathFieldToLatex)
      const mathContentSpans = wysiwyg.querySelectorAll(
        'span.math-content[data-latex]',
      )

      mathContentSpans.forEach((span: HTMLElement) => {
        try {
          // Get LaTeX from data attribute
          const latex = span.getAttribute('data-latex')

          if (latex) {
            // Create new editable mathfield with the LaTeX
            const mf = createMathField(latex)

            // Replace the math-content span with editable mathfield
            if (span.parentNode) {
              span.parentNode.replaceChild(mf, span)
            }

            // Configure the new mathfield
            setTimeout(() => {
              try {
                if (document.contains(mf)) {
                  configureMathField(mf)
                }
              } catch (error) {
                console.warn(
                  'Failed to configure mathfield after insertion:',
                  error,
                )
              }
            }, 50)
          }
        } catch (error) {
          console.warn('Failed to process math-content span:', error)
        }
      })
    } catch (error) {
      console.warn('Failed to process existing content:', error)
    }
  }, [createMathField, configureMathField])

  /**
   * Insert a new mathfield into the editor
   */
  const insertMathField = useCallback(() => {
    if (!editorInstanceRef.current || !editorInstanceRef.current.insertHTML) {
      console.warn('Editor instance not ready for mathfield insertion')
      return
    }

    const mf = createMathField('')

    const wrapper = document.createElement('span')
    wrapper.appendChild(mf)
    wrapper.appendChild(document.createTextNode('\u00A0'))

    try {
      editorInstanceRef.current.insertHTML(wrapper.innerHTML)
    } catch (error) {
      console.warn('Failed to insert mathfield:', error)
      return
    }

    requestAnimationFrame(() => {
      try {
        if (
          !editorInstanceRef.current ||
          !editorInstanceRef.current.getContents
        ) {
          console.warn('Editor instance not ready for content retrieval')
          return
        }

        const current = editorInstanceRef.current.getContents()

        // Only update if content actually changed
        const currentMarkdown = editorHtmlToMarkdown(current)
        if (currentMarkdown !== lastContentRef.current) {
          setEditorContent(current)
          emitChange(current)
        }

        const wysiwyg =
          editorInstanceRef.current.core?.context?.element?.wysiwyg
        if (!wysiwyg) {
          console.warn('WYSIWYG element not found')
          return
        }

        const fields = wysiwyg.querySelectorAll('math-field.editing')

        if (fields.length > 0) {
          const lastField = fields[fields.length - 1] as MathfieldElement

          if (document.contains(lastField)) {
            focusedMathFieldRef.current = lastField
            configureMathField(lastField)

            // Focus and show keyboard immediately for better UX
            setTimeout(() => {
              if (document.contains(lastField) && lastField.focus) {
                // Reset manual close flag - new math field insertion should show keyboard
                manuallyClosedRef.current = false

                lastField.focus()
                lastField.click() // Force focus

                // Auto-show virtual keyboard after focus
                setTimeout(() => {
                  try {
                    const keyboard = (window as any).mathVirtualKeyboard
                    if (keyboard) {
                      keyboard.show()
                      // Inject close button after keyboard is shown
                      // setTimeout(() => {
                      //   injectCloseButtonToKeyboard()
                      // }, 150)
                    }
                  } catch (error) {
                    // Silently ignore keyboard errors
                  }
                }, 100)
              }
            }, 50)
          }
        }
      } catch (error) {
        console.warn(
          'insertMathField: failed to insert or retrieve editor content:',
          error,
        )
      }
    })
  }, [createMathField, configureMathField, emitChange, editorHtmlToMarkdown])

  useEffect(() => {
    if (!editorInstanceRef.current) return
    const root: HTMLElement =
      editorInstanceRef.current.core.context.element.wysiwyg

    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement
      if (
        target &&
        target.tagName &&
        target.tagName.toLowerCase() === 'math-field'
      ) {
        isEditingMath.current = true // Set flag on focus
        focusedMathFieldRef.current = target as MathfieldElement

        // Check if keyboard was manually closed - if so, don't auto-reopen
        if (manuallyClosedRef.current) {
          return
        }

        // Auto-show virtual keyboard
        setTimeout(() => {
          try {
            const mf = target as MathfieldElement
            // Always use window.mathVirtualKeyboard as recommended by MathLive
            if ((window as any).mathVirtualKeyboard) {
              ;(window as any).mathVirtualKeyboard.show()
            }

            // Force keyboard position to bottom after showing
            setTimeout(() => {
              const keyboard = document.querySelector('.ML__keyboard')
              if (keyboard) {
                const kb = keyboard as HTMLElement
                kb.style.position = 'fixed'
                kb.style.bottom = '0'
                kb.style.top = 'auto'
                kb.style.left = '0'
                kb.style.right = '0'
                kb.style.transform = 'none'
                kb.style.margin = '0'

                // Inject close button
                // injectCloseButtonToKeyboard()

                // Scroll mathfield into view above keyboard
                if (mf && mf.scrollIntoView) {
                  setTimeout(() => {
                    try {
                      mf.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                      })
                    } catch (error) {
                      // Fallback to simple scrollIntoView
                      try {
                        mf.scrollIntoView(false)
                      } catch (e) {
                        // Ignore scroll errors
                      }
                    }
                  }, 200)
                }
              }
            }, 150)
          } catch (error) {
            console.warn(
              'Failed to show virtual keyboard for mathfield:',
              error,
            )
          }
        }, 100)
      }
    }

    // Handle clicks on keyboard toggle button in mathfield
    const handleKeyboardToggle = (e: MouseEvent) => {
      const target = e.target as HTMLElement

      // Check if click is on MathLive keyboard toggle button
      if (
        target.classList.contains('ML__virtual-keyboard-toggle') ||
        target.closest('.ML__virtual-keyboard-toggle') ||
        target.closest('[part="virtual-keyboard-toggle"]')
      ) {
        e.preventDefault()
        e.stopPropagation()

        try {
          // Reset manual close flag - user is intentionally opening keyboard
          manuallyClosedRef.current = false

          // Find the associated math-field and focus it first
          const mathField = target.closest('math-field') as MathfieldElement
          if (mathField && document.contains(mathField)) {
            mathField.focus()
            focusedMathFieldRef.current = mathField
          }

          // Always use window.mathVirtualKeyboard
          const keyboard = (window as any).mathVirtualKeyboard
          if (keyboard) {
            if (keyboard.visible) {
              keyboard.hide()
            } else {
              keyboard.show()
              // Force position after showing - need longer delay to ensure keyboard is fully rendered
              setTimeout(() => {
                const kb = document.querySelector(
                  '.ML__keyboard',
                ) as HTMLElement
                if (kb) {
                  kb.style.position = 'fixed'
                  kb.style.bottom = '0'
                  kb.style.top = 'auto'
                  kb.style.left = '0'
                  kb.style.right = '0'
                  kb.style.transform = 'none'
                  kb.style.margin = '0'

                  // Inject close button after keyboard is fully rendered
                  // setTimeout(() => {
                  //   injectCloseButtonToKeyboard()
                  // }, 100)
                }
              }, 150)
            }
          }
        } catch (error) {
          console.warn('Failed to toggle virtual keyboard:', error)
        }
      }
    }

    const handleMathFieldBlur = (e: FocusEvent) => {
      const blurredElement = e.target as HTMLElement

      // Exit if the blurred element is not a math-field
      if (blurredElement?.tagName?.toLowerCase() !== 'math-field') {
        return
      }

      const blurredMathfield = e.target as MathfieldElement
      // Use a timeout to allow clicks on the virtual keyboard without causing a blur-conversion
      setTimeout(() => {
        if (document.activeElement?.tagName.toLowerCase() !== 'math-field') {
          isEditingMath.current = false // Lower the flag

          // Reset manual close flag - natural blur means user is done with this math field
          manuallyClosedRef.current = false

          // Hide virtual keyboard when blur out of mathfield
          try {
            const keyboard = (window as any).mathVirtualKeyboard
            if (keyboard && keyboard.visible) {
              keyboard.hide()
            }
          } catch (error) {
            console.warn('Failed to hide virtual keyboard on blur:', error)
          }

          // Convert the field that was just blurred
          convertMathFieldToLatex(blurredMathfield)
        }
      }, 200)
    }

    const handleInput = (e: Event) => {
      const target = e.target as HTMLElement
      if (
        target &&
        target.tagName &&
        target.tagName.toLowerCase() === 'math-field'
      ) {
        // Reset manual close flag - user is actively typing, allow keyboard to auto-show next time
        manuallyClosedRef.current = false

        // This is where we would sync the content if we wanted live updates.
        // For now, we do nothing and sync on blur.
      }
    }

    // Handle keyboard events for easier deletion
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement

      // Handle Delete/Backspace on editor DIV - check for empty math-fields
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        (target.classList.contains('se-wrapper-wysiwyg') ||
          target.classList.contains('sun-editor-editable'))
      ) {
        const mathFields = target.querySelectorAll('math-field')

        mathFields.forEach(field => {
          const mf = field as MathfieldElement
          const value = mf.value || ''

          // If empty, remove it
          if (
            !value ||
            value.trim() === '' ||
            value.includes('\\placeholder')
          ) {
            if (mf.parentNode) {
              mf.parentNode.removeChild(mf)

              // Hide keyboard
              try {
                const keyboard = (window as any).mathVirtualKeyboard
                if (keyboard) {
                  keyboard.hide()
                }
              } catch (error) {
                // Ignore
              }

              // Update editor content
              setTimeout(() => {
                if (editorInstanceRef.current?.getContents) {
                  const updatedContent = editorInstanceRef.current.getContents()
                  setEditorContent(updatedContent)
                  emitChange(updatedContent)
                }
              }, 50)
            }
          }
        })
      }

      // Handle Delete/Backspace on rendered LaTeX spans
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        target.classList.contains('math-content')
      ) {
        e.preventDefault()
        e.stopPropagation()

        // Remove the math span
        const mathSpan = target.closest('.math-content')
        if (mathSpan && mathSpan.parentNode) {
          mathSpan.parentNode.removeChild(mathSpan)

          // Update editor content
          setTimeout(() => {
            if (editorInstanceRef.current?.getContents) {
              const updatedContent = editorInstanceRef.current.getContents()
              setEditorContent(updatedContent)
              emitChange(updatedContent)
            }
          }, 50)
        }
      }

      // Handle Delete/Backspace inside math-field for better UX
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        target.tagName?.toLowerCase() === 'math-field'
      ) {
        const mf = target as MathfieldElement

        // Helper function to check if math-field is truly empty
        const checkIsEmpty = (field: MathfieldElement): boolean => {
          const value = field.value || ''
          const trimmedValue = value.trim()

          return (
            trimmedValue === '' ||
            trimmedValue === '\\placeholder' ||
            value.includes('\\placeholder')
          )
        }

        // Check IMMEDIATELY if already empty - user clicked f(x) by mistake
        if (checkIsEmpty(mf)) {
          e.preventDefault()
          e.stopPropagation()

          if (mf.parentNode) {
            mf.parentNode.removeChild(mf)

            // Hide keyboard
            try {
              const keyboard = (window as any).mathVirtualKeyboard
              if (keyboard) {
                keyboard.hide()
              }
            } catch (error) {
              // Ignore
            }

            // Update editor content
            setTimeout(() => {
              if (editorInstanceRef.current?.getContents) {
                const updatedContent = editorInstanceRef.current.getContents()
                setEditorContent(updatedContent)
                emitChange(updatedContent)
              }
            }, 50)
          }
        } else {
          // If not empty, let MathLive handle the deletion first
          // Then check if it became empty after deletion
          setTimeout(() => {
            if (checkIsEmpty(mf) && mf.parentNode) {
              mf.parentNode.removeChild(mf)

              // Hide keyboard
              try {
                const keyboard = (window as any).mathVirtualKeyboard
                if (keyboard) {
                  keyboard.hide()
                }
              } catch (error) {
                // Ignore
              }

              // Update editor content
              setTimeout(() => {
                if (editorInstanceRef.current?.getContents) {
                  const updatedContent = editorInstanceRef.current.getContents()
                  setEditorContent(updatedContent)
                  emitChange(updatedContent)
                }
              }, 50)
            }
          }, 10)
        }
      }

      // Allow Escape to close mathfield without converting
      if (e.key === 'Escape') {
        const mf = target as MathfieldElement
        if (target.tagName?.toLowerCase() === 'math-field') {
          e.preventDefault()

          // If empty, remove the mathfield
          if (!mf.value || mf.value.trim() === '') {
            if (mf.parentNode) {
              mf.parentNode.removeChild(mf)
            }
          } else {
            // Convert to latex and blur
            mf.blur()
          }

          // Hide keyboard
          try {
            const keyboard = (window as any).mathVirtualKeyboard
            if (keyboard) {
              keyboard.hide()
            }
          } catch (error) {
            // Ignore
          }
        }
      }
    }

    // Handle click on LaTeX spans to convert back to editable mathfield
    // Use a flag to prevent infinite loops
    let isConverting = false
    const handleClick = (e: MouseEvent) => {
      // Prevent multiple simultaneous conversions
      if (isConverting) {
        return
      }

      const target = e.target as HTMLElement

      // Check if clicking on keyboard icon inside empty math-field
      if (
        target.classList.contains('ML__virtual-keyboard-toggle') ||
        target.closest('.ML__virtual-keyboard-toggle')
      ) {
        const mathField = target.closest('math-field') as MathfieldElement
        if (mathField) {
          const value = mathField.value || ''

          // If double-click on keyboard icon of empty field, remove it
          if (e.detail === 2 && (!value || value.trim() === '')) {
            e.preventDefault()
            e.stopPropagation()

            if (mathField.parentNode) {
              mathField.parentNode.removeChild(mathField)

              // Hide keyboard
              try {
                const keyboard = (window as any).mathVirtualKeyboard
                if (keyboard) {
                  keyboard.hide()
                }
              } catch (error) {
                // Ignore
              }

              // Update editor content
              setTimeout(() => {
                if (editorInstanceRef.current?.getContents) {
                  const updatedContent = editorInstanceRef.current.getContents()
                  setEditorContent(updatedContent)
                  emitChange(updatedContent)
                }
              }, 50)
            }
            return
          }
        }
      }

      // Don't convert if clicking on delete button
      if (
        target.classList.contains('math-delete-btn') ||
        target.closest('.math-delete-btn')
      ) {
        return
      }

      const mathContent = target.closest('.math-content')

      if (mathContent && mathContent.classList.contains('math-content')) {
        // Check if already being converted or if it's already a mathfield
        if (mathContent.querySelector('math-field')) {
          return
        }

        e.preventDefault()
        e.stopPropagation()
        isConverting = true

        const latexSpan = mathContent as HTMLElement
        // Get LaTeX from data attribute or from katex-mathml span
        const latex =
          latexSpan.getAttribute('data-latex') ||
          latexSpan.querySelector('span.katex-mathml')?.textContent ||
          ''

        if (latex && latexSpan.parentNode) {
          try {
            // Create new mathfield with the LaTeX
            const mf = createMathField(latex)

            // Replace LaTeX span with mathfield
            latexSpan.parentNode.replaceChild(mf, latexSpan)

            // Configure and focus the mathfield
            setTimeout(() => {
              try {
                if (document.contains(mf)) {
                  configureMathField(mf)
                  mf.focus()
                }
              } catch (error) {
                console.warn(
                  'Failed to configure mathfield after click:',
                  error,
                )
              } finally {
                // Reset flag after a delay to allow DOM to settle
                setTimeout(() => {
                  isConverting = false
                }, 100)
              }
            }, 50)
          } catch (error) {
            console.warn('Failed to convert LaTeX span to mathfield:', error)
            isConverting = false
          }
        } else {
          isConverting = false
        }
      }
    }

    root.addEventListener('focus', handleFocus, true)
    root.addEventListener('blur', handleMathFieldBlur, true)
    root.addEventListener('input', handleInput, true)
    root.addEventListener('click', handleClick, true)
    root.addEventListener('click', handleKeyboardToggle, true)
    root.addEventListener('keydown', handleKeyDown, true)

    return () => {
      root.removeEventListener('focus', handleFocus, true)
      root.removeEventListener('blur', handleMathFieldBlur, true)
      root.removeEventListener('input', handleInput, true)
      root.removeEventListener('click', handleClick, true)
      root.removeEventListener('click', handleKeyboardToggle, true)
      root.removeEventListener('keydown', handleKeyDown, true)

      if (conversionTimeoutRef.current) {
        clearTimeout(conversionTimeoutRef.current)
        conversionTimeoutRef.current = null
      }
      focusedMathFieldRef.current = null
    }
  }, [convertMathFieldToLatex, createMathField, configureMathField, emitChange])

  // Monitor and force keyboard position to bottom
  useEffect(() => {
    const container = containerRef.current
    const wrapper = container?.querySelector('.sun-editor-wrapper')

    let isUpdating = false
    let lastUpdateTime = 0
    const THROTTLE_MS = 100 // Throttle to prevent excessive updates

    const forceKeyboardPosition = () => {
      // Throttle to prevent excessive updates
      const now = Date.now()
      if (isUpdating || now - lastUpdateTime < THROTTLE_MS) {
        return
      }

      isUpdating = true
      lastUpdateTime = now

      try {
        const keyboards = document.querySelectorAll(
          '.ML__keyboard, [class*="ML__keyboard"]',
        )
        let keyboardVisible = false

        keyboards.forEach(kb => {
          const element = kb as HTMLElement
          if (element && element.offsetParent !== null) {
            element.style.position = 'fixed'
            element.style.bottom = '0'
            element.style.top = 'auto'
            element.style.left = '0'
            element.style.right = '0'
            element.style.transform = 'none'
            element.style.margin = '0'

            // Check if keyboard is visible
            if (
              element.classList.contains('is-visible') ||
              element.offsetHeight > 0
            ) {
              keyboardVisible = true
            }
          }
        })

        // Add/remove padding based on keyboard visibility
        if (container) {
          if (keyboardVisible) {
            container.classList.add('keyboard-visible')
          } else {
            container.classList.remove('keyboard-visible')
          }
        }

        if (wrapper) {
          if (keyboardVisible) {
            wrapper.classList.add('keyboard-visible')
          } else {
            wrapper.classList.remove('keyboard-visible')
          }
        }
      } finally {
        setTimeout(() => {
          isUpdating = false
        }, THROTTLE_MS)
      }
    }

    // Run immediately and then periodically (less frequent)
    forceKeyboardPosition()
    const interval = setInterval(forceKeyboardPosition, 1000) // Increased to 1s to reduce ResizeObserver triggers

    // Also observe DOM changes but with heavy debouncing
    let observerTimeout: NodeJS.Timeout | null = null
    const observer = new MutationObserver(() => {
      // Heavy debounce to prevent ResizeObserver loop
      if (observerTimeout) {
        clearTimeout(observerTimeout)
      }
      observerTimeout = setTimeout(() => {
        forceKeyboardPosition()
      }, 300) // Increased debounce delay
    })

    // Only observe body for keyboard elements (reduce monitoring)
    observer.observe(document.body, {
      childList: true,
      subtree: false, // Only direct children to reduce overhead
    })

    return () => {
      clearInterval(interval)
      if (observerTimeout) {
        clearTimeout(observerTimeout)
      }
      observer.disconnect()
      // Cleanup classes on unmount
      if (container) {
        container.classList.remove('keyboard-visible')
      }
      if (wrapper) {
        wrapper.classList.remove('keyboard-visible')
      }
    }
  }, [])

  // Function to inject math button into toolbar
  const injectMathButton = useCallback(() => {
    if (!editorInstanceRef.current) {
      return false
    }

    const core = editorInstanceRef.current.core
    if (!core) {
      console.warn('[BrainlyMathEditor] No core in editor instance')
      return false
    }

    const context = core.context
    if (!context) {
      console.warn('[BrainlyMathEditor] No context in core')
      return false
    }

    // Try multiple ways to find toolbar
    let toolbar = context.toolbar

    if (!toolbar) {
      const container = context.element?.container
      if (container) {
        toolbar = container.querySelector('.se-toolbar')
      }
    }

    if (!toolbar) {
      const wysiwygFrame = context.element?.wysiwygFrame
      if (wysiwygFrame?.parentElement) {
        // Try parentElement first
        toolbar = wysiwygFrame.parentElement.querySelector('.se-toolbar')

        // If not found, try going up the parent chain
        if (!toolbar) {
          let current = wysiwygFrame.parentElement
          while (current && !toolbar) {
            toolbar = current.querySelector('.se-toolbar')
            if (toolbar) {
              break
            }
            current = current.parentElement
            if (current?.classList?.contains('se-wrapper')) {
              break
            }
          }
        }

        // Also try previous sibling (toolbar might be before wysiwygFrame)
        if (!toolbar && wysiwygFrame.parentElement) {
          let sibling = wysiwygFrame.previousElementSibling
          while (sibling) {
            if (sibling.classList?.contains('se-toolbar')) {
              toolbar = sibling as HTMLElement
              break
            }
            sibling = sibling.previousElementSibling
          }
        }
      }
    }

    // Try to find toolbar by querying the editor wrapper
    if (!toolbar && context.element?.wysiwygFrame) {
      // Find the wrapper element that contains both toolbar and wysiwyg
      const wrapper = context.element.wysiwygFrame.closest(
        '.se-wrapper, .sun-editor',
      )
      if (wrapper) {
        toolbar = wrapper.querySelector('.se-toolbar')
      }
    }

    // Last resort: query all toolbars and find the one related to this editor
    if (!toolbar) {
      const allToolbars = document.querySelectorAll('.se-toolbar')

      // Try to match toolbar with wysiwygFrame by finding common parent
      if (context.element?.wysiwygFrame) {
        const wysiwygParent = context.element.wysiwygFrame.closest(
          '.se-wrapper, .sun-editor',
        )
        for (let i = 0; i < allToolbars.length; i++) {
          const tb = allToolbars[i] as HTMLElement
          const toolbarParent = tb.closest('.se-wrapper, .sun-editor')
          if (
            wysiwygParent &&
            toolbarParent &&
            wysiwygParent === toolbarParent
          ) {
            toolbar = tb
            break
          }
        }
      }
    }

    if (!toolbar) {
      console.warn(
        '[BrainlyMathEditor] Could not find toolbar after all attempts',
      )
      return false
    }

    // Check if button already exists
    const existingButton = toolbar.querySelector('.se-btn-math-insert')
    if (existingButton) {
      return true
    }

    // Create and inject button
    const mathInsertBtn = document.createElement('button')
    mathInsertBtn.className = 'se-btn se-btn-math-insert'
    mathInsertBtn.title = 'Insert formula'

    // Create a container for the React icon
    const iconContainer = document.createElement('span')
    iconContainer.className = 'se-icon-math'
    mathInsertBtn.appendChild(iconContainer)

    // Render the MUI icon using ReactDOM
    ReactDOM.render(<FunctionsIcon sx={{ fontSize: 18 }} />, iconContainer)

    mathInsertBtn.onclick = e => {
      e.preventDefault()
      e.stopPropagation()
      insertMathField()
    }

    // Find a suitable button to insert after
    // Try video button first
    let targetButton =
      toolbar.querySelectorAll('.se-btn')[
        toolbar.querySelectorAll('.se-btn').length - 1
      ]
    // If no video button, try to find list button
    if (!targetButton) {
      targetButton = toolbar.querySelector('[data-command="list"]')
    }

    // If still no button, try to find any button in the toolbar
    if (!targetButton) {
      targetButton = toolbar.querySelector('.se-btn')
    }

    if (targetButton && targetButton.parentNode) {
      // Insert after target button in the same group
      targetButton.parentNode.insertBefore(
        mathInsertBtn,
        targetButton.nextSibling,
      )
    } else {
      // Try to find first visible button group
      const buttonGroups = toolbar.querySelectorAll('.se-btn-list')
      let inserted = false

      // Try to append to first visible group
      for (let i = 0; i < buttonGroups.length; i++) {
        const group = buttonGroups[i] as HTMLElement
        // Check if group is visible (has display other than none)
        const style = window.getComputedStyle(group)
        if (style.display !== 'none' && group.children.length > 0) {
          group.appendChild(mathInsertBtn)
          inserted = true
          break
        }
      }

      if (!inserted) {
        // Fallback: append to last button group or create new one
        if (buttonGroups.length > 0) {
          const lastGroup = buttonGroups[buttonGroups.length - 1]
          lastGroup.appendChild(mathInsertBtn)
        } else {
          // Create a new button group
          const newGroup = document.createElement('div')
          newGroup.className = 'se-btn-list'
          newGroup.appendChild(mathInsertBtn)
          toolbar.appendChild(newGroup)
        }
      }
    }

    setButtonsInjected(true)
    return true
  }, [insertMathField])

  // Inject button when editor instance is ready
  useEffect(() => {
    if (!editorInstanceRef.current || buttonsInjected) {
      return
    }

    // Try immediate injection
    if (injectMathButton()) {
      return
    }

    // Retry with delays - longer delays to ensure toolbar is ready
    const timeouts: NodeJS.Timeout[] = []
    for (let i = 1; i <= 10; i++) {
      const timeout = setTimeout(() => {
        if (!buttonsInjected && editorInstanceRef.current) {
          injectMathButton()
        }
      }, i * 300)
      timeouts.push(timeout)
    }

    return () => {
      timeouts.forEach(clearTimeout)
    }
  }, [injectMathButton, buttonsInjected])

  // Sync editorContent to editor instance (only for external changes)
  useEffect(() => {
    if (!editorInstanceRef.current) return

    // Skip if this is an internal change (user typing)
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false
      return
    }

    // Only set content if different from current editor content
    const currentContent = editorInstanceRef.current.getContents?.() || ''
    if (editorContent && editorContent !== currentContent) {
      // This is an external change (from props), update editor
      editorInstanceRef.current.setContents(editorContent)
      initialContentSetRef.current = true

      // Process LaTeX if needed
      if (!readOnly) {
        setTimeout(() => {
          processExistingContent()
        }, 100)
      }
    }
  }, [editorContent, readOnly, processExistingContent])

  // Sync value prop changes to editor (markdown → HTML conversion)
  useEffect(() => {
    // Skip if value is same as the value we just emitted
    if (value === lastSyncedValueRef.current) {
      return
    }

    // Value changed from outside (external change) → convert to HTML and sync to editor
    const htmlContent = markdownToEditorHtml(value)
    lastSyncedValueRef.current = value
    lastContentRef.current = value
    setEditorContent(htmlContent)
  }, [value, markdownToEditorHtml])

  // Process existing content when value prop changes (for loading saved content)
  useEffect(() => {
    if (!editorInstanceRef.current || !value || readOnly) {
      return
    }

    // Process LaTeX spans in the editor after content is set
    if (!readOnly) {
      const timer = setTimeout(() => {
        if (editorInstanceRef.current) {
          processExistingContent()
        }
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [value, readOnly, processExistingContent])

  const editorOptions = useMemo(() => {
    // Ensure buttonList is always a valid array
    const safeButtonList =
      Array.isArray(buttonList) && buttonList.length > 0
        ? buttonList
        : BUTTON_LIST

    return {
      mode: mode,
      rtl: false,
      height: typeof height === 'number' ? `${height}px` : height,
      minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
      placeholder,
      tagsBlacklist: 'h1|h2|h3|h4|h5|h6|iframe',
      showPathLabel: false,
      resizingBar: true,
      resizeEnable: true, // Enable manual resize
      stickyToolbar: -1, // Disable sticky to allow proper resizing (-1 = disabled)
      // Character counter - only enable if maxCharCount is set
      charCounter: maxCharCount ? true : false,
      charCounterLabel: 'Characters:',
      // Set max char count if provided
      ...(maxCharCount && {
        maxCharCount: maxCharCount,
      }),
      width: '100%',
      // Enable resize functionality
      disableToolbar: false,
      // Error handling
      onError: (error: any) => {
        console.warn('SunEditor error:', error)
      },
      buttonList: safeButtonList,
      katex: katex,
      // Allow custom elements
      addTagsWhitelist: WHITE_LIST_TAGS,
      allowedTags: ALLOWED_TAGS,
      keepClasses: true,
      keepAttr: true,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      defaultStyle: DEFAULT_STYLE,
      // Image upload configuration - simplified for better UX
      imageFileInput: true, // Enable file upload
      imageUrlInput: false, // Disable URL input (forces upload via handler)
      imageResizing: false,
      imageHeightShow: false,
      imageAlignShow: false,
      imageUploadSizeLimit: 10485760, // 10MB limit
      // Simplify image dialog - hide unnecessary options
      imageWidth: 'auto', // Default width
      imageHeight: 'auto', // Default height
      imageSizeOnlyPercentage: false, // Allow auto/pixel values
      imageRotation: false, // Disable rotation option
      // Hide detailed image options to simplify the upload dialog
    }
  }, [height, minHeight, placeholder, maxCharCount, mode, buttonList])

  // ============================================================================
  // Render: Use MarkdownViewer for readOnly, SunEditor for editing
  // ============================================================================

  if (readOnly) {
    return (
      <MarkdownViewer
        content={value}
        height={height}
        minHeight={minHeight}
        className="brainly-math-editor-readonly"
      />
    )
  }

  return (
    <div className="brainly-math-editor-container" ref={containerRef}>
      {/* Main Editor with integrated MathLive buttons */}
      <div className="sun-editor-wrapper">
        <SunEditor
          defaultValue={editorContent}
          onChange={handleChange}
          setOptions={editorOptions}
          getSunEditorInstance={(instance: any) => {
            editorInstanceRef.current = instance
          }}
          disable={disabled}
          readOnly={readOnly}
          autoFocus={false}
          onImageUploadBefore={handleImageUploadBefore as any}
          onImageUploadError={(message: any, result: any) => {
            console.error('[BrainlyMathEditor] Image upload error:', {
              message,
              result,
            })
          }}
          onLoad={() => {
            // The editor is fully loaded, now we can safely process the content.
            processExistingContent()
            // Try to inject math button after editor is fully loaded
            setTimeout(() => {
              if (!buttonsInjected) {
                injectMathButton()
              }
            }, 500)
          }}
        />
      </div>
    </div>
  )
}

export default BrainlyMathEditor

// Export BrainlyMathEditorSimple as wrapper for backward compatibility
export const BrainlyMathEditorSimple: FC<BrainlyMathEditorProps> = props => {
  return <BrainlyMathEditor {...props} variant="simple" />
}
